
  import { defineComponent, onMounted, ref } from 'vue';
  import { ErrorMessage, Field, Form } from 'vee-validate';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { useRouter, useRoute } from 'vue-router';
  import { Actions } from '@/store/enums/StoreEnums';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import * as Yup from 'yup';
  import MonacoEditor from '@guolao/vue-monaco-editor';

  export default defineComponent({
    name: 'newsletter-sections-market-activity-editing',
    components: { ErrorMessage, Field, Form, MonacoEditor },
    async setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const route = useRoute();
      const { can } = useAbility();
      const submitButton = ref<HTMLButtonElement | null>(null);
      const thumbnail = ref('/media/avatars/blank-image.jpg');

      const translate = (text: string) => (te(text) ? t(text) : text);

      onMounted(() => {
        setCurrentPageBreadcrumbs(translate('newslettersSectionUpdating'), [
          translate('assets'),
        ]);
      });

      const { data } = await store.dispatch(
        Actions.GET_NEWSLETTER,
        route.params.id
      );
      const newsletter = ref<any>(data.data);

      const parsedVariables = newsletter.value.variables
        ? JSON.parse(newsletter.value.variables)
        : {};

      newsletter.value.variables = parsedVariables;

      const htmlContentPreview = newsletter.value.htmlContent.replace(
        /{{\s*([^}]+)\s*}}/g,
        (_, variable) => parsedVariables[variable.trim()] || ''
      );

      const previewImage = (e: Event) => {
        const input = e.target as HTMLInputElement;
        if (input.files && input.files[0]) {
          const file = input.files[0];
          thumbnail.value = URL.createObjectURL(file);
          newsletter.value.image = file;
        } else {
          newsletter.value.image = null;
        }
      };

      const newslettersSchema = Yup.object().shape({
        title: Yup.string()
          .required(() => translate('EN_TITLE_IS_REQUIRED_FIELD'))
          .label('title'),
        key: Yup.string()
          .required(() => translate('EN_KEY_IS_REQUIRED_FIELD'))
          .label('key'),
      });

      const goBack = () => router.back();

      const onSubmitUpdate = async (values) => {
        if (submitButton.value) {
          submitButton.value.disabled = true;
          submitButton.value.setAttribute('data-kt-indicator', 'on');
        }

        if (values.sort) values.sort = Number(values.sort);

        const payload = {
          ...values,
          htmlContent: newsletter.value.htmlContent,
          variables: {
            image: values.image ?? newsletter.value.variables.image,
            subtitle: newsletter.value.variables.subtitle,
            paragraph2: newsletter.value.variables.paragraph2,
          },
        };

        delete payload.image;

        await store.dispatch(Actions.UPDATE_NEWSLETTER, {
          id: route.params.id,
          data: payload,
        });

        if (values.image instanceof File) {
          const formData = new FormData();
          formData.append('image', values.image);
          await store.dispatch(Actions.UPLOAD_HERO_BANNER_IMAGE, {
            id: route.params.id,
            data: formData,
          });
        }

        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];

        if (error) {
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: { confirmButton: 'btn fw-bold btn-light-danger' },
          });
          submitButton.value?.removeAttribute('data-kt-indicator');
          submitButton.value!.disabled = false;
        } else {
          Swal.fire({
            text: translate('SUCCESSFULLY_UPDATED_NEWSLETTER_SECTION'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: { confirmButton: 'btn btn-light-primary' },
          }).then(() => router.push({ name: 'newsletter-sections-listing' }));
        }
      };

      store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');

      return {
        onSubmitUpdate,
        submitButton,
        translate,
        newslettersSchema,
        goBack,
        newsletter,
        can,
        thumbnail,
        previewImage,
        parsedVariables,
        htmlContentPreview,
      };
    },
  });
